import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Lock, Check, X, Printer } from "lucide-react";
import { useI18n } from "../i18n";
import api, { fmtErr } from "../api";
import { LLC, isLLCFormed } from "../legal";

export default function Founding() {
  const { lang } = useI18n();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    contact_name: "",
    contact_email: "",
    company_name: "",
    role: "",
    plant_location: "",
    employees: "",
    main_capabilities: "",
    why_founding: "",
    phone: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data));
  }, []);

  function set(k, v) { setForm((p) => ({ ...p, [k]: v })); }

  async function onSubmit(e) {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/founding-applications", form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      // refresh stats
      const { data } = await api.get("/public/stats");
      setStats(data);
    } catch (e) {
      setErr(fmtErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  const spotsLeft = stats?.founding_spots_remaining ?? 10;
  const spotsClaimed = stats?.founding_spots_claimed ?? 0;
  const pct = ((10 - spotsLeft) / 10) * 100;

  const copy = {
    en: {
      kicker: "INVITATION-ONLY · CORRIDOR FOUNDING PROGRAM",
      title: "10 manufacturers.\nOne corridor.\nA permanent advantage.",
      sub: "We selected ten mid-size IMMEX manufacturers in Juárez to define the next decade of cross-border procurement. This is one of those invitations.",
      spotsLeftL: "FOUNDING SPOTS REMAINING",
      sectionWhy: "WHY THE FIRST 10 GET EVERYTHING",
      reasons: [
        { t: "Lifetime founding price", d: "Lock $149/month forever — vs $599 standard Premium. Save $5,400/year for as long as you stay subscribed." },
        { t: "Corridor Verified ✓ from day one", d: "Skip the audit queue. Featured placement in directory, immediate Premium tier badge. US buyers see you first." },
        { t: "White-glove onboarding", d: "We come to your plant. We photograph it. We translate your docs. We write your bilingual profile. Six weeks, fully done-for-you." },
        { t: "Featured in the Supplier Digest", d: "Monthly bilingual email to 1,200+ US procurement managers. Founding 10 always get top placement. Forever." },
        { t: "Direct line to the founder", d: "Personal WhatsApp access to Derick Varela. No ticket queue. Strategy calls included." },
        { t: "Lifetime API + integrations roadmap input", d: "What we build next — Cross-Border Data Bridge, US compliance integrations, custom AI — you tell us. You vote." },
      ],
      compareTitle: "FOUNDING 10 vs STANDARD PREMIUM",
      compare: [
        ["Lifetime price lock at $149/mo", true, false],
        ["Onsite plant visit + photography", true, false],
        ["Done-for-you bilingual profile", true, false],
        ["Featured in monthly Supplier Digest", true, false],
        ["Top of corridor directory", true, false],
        ["Founder WhatsApp access", true, false],
        ["Roadmap voting rights", true, false],
        ["Cross-Border Data Bridge (US partner KPIs)", true, true],
        ["Document vault + share links", true, true],
        ["RFQ inbox", true, true],
        ["Bilingual ES/EN profile", true, true],
      ],
      caseStudy: "CASE STUDY · INDUSTRIAS FRONTERA MX",
      caseStudyQuote: "They told us nobody targets maquiladoras. We told them — exactly.",
      caseStudyAttrib: "— Derick Varela, Founder · Apex Digital",
      caseStudyStats: [
        ["$2.4M", "Initial contract value"],
        ["6 weeks", "From walk-in to live bilingual platform"],
        ["12", "Page-1 keyword rankings, EN + ES"],
        ["4", "Qualified US procurement inquiries in first 30 days"],
      ],
      formTitle: "Reserve your founding spot",
      formSub: "Submitting an application doesn't lock you in. It puts your factory in front of us for review. We respond within 48 hours.",
      submittedTitle: "Application received.",
      submittedBody: "We'll review and respond within 48 hours. If selected, you'll receive a calendar link for a strategy call with Derick.",
      f: {
        contact_name: "Your name",
        contact_email: "Email",
        company_name: "Company name",
        role: "Your role (e.g. Director General, Gerente Comercial)",
        plant_location: "Plant location (city / industrial park)",
        employees: "Number of employees",
        main_capabilities: "Main capabilities (e.g. CNC, injection molding, harnesses)",
        why_founding: "Why you want to be one of the first 10 (be honest, this is what we read)",
        phone: "WhatsApp / phone",
      },
      submit: "Submit application",
      footerNote: "Founding pricing ends when the 10th spot is filled. No exceptions. No extensions.",
    },
    es: {
      kicker: "POR INVITACIÓN · PROGRAMA FUNDADORES DEL CORREDOR",
      title: "10 fabricantes.\nUn corredor.\nUna ventaja permanente.",
      sub: "Seleccionamos diez fabricantes IMMEX medianos en Juárez para definir la próxima década de compras transfronterizas. Esta es una de esas invitaciones.",
      spotsLeftL: "LUGARES FUNDADORES DISPONIBLES",
      sectionWhy: "POR QUÉ LOS PRIMEROS 10 SE LO LLEVAN TODO",
      reasons: [
        { t: "Precio fundador de por vida", d: "Asegura $149/mes para siempre — vs $599 estándar Premium. Ahorras $5,400/año mientras estés suscrito." },
        { t: "Corredor Verificado ✓ desde el día uno", d: "Saltas la fila de auditoría. Posición destacada, insignia Premium inmediata. Los compradores te ven primero." },
        { t: "Onboarding de alto contacto", d: "Vamos a tu planta. La fotografiamos. Traducimos tus documentos. Escribimos tu perfil bilingüe. Seis semanas, todo hecho." },
        { t: "Destacado en el Supplier Digest", d: "Boletín mensual bilingüe a 1,200+ gerentes de compras en EE.UU. Los 10 fundadores siempre arriba. Para siempre." },
        { t: "Línea directa con el fundador", d: "Acceso personal a WhatsApp de Derick Varela. Sin filas. Llamadas estratégicas incluidas." },
        { t: "Voz en el roadmap de por vida", d: "Lo que construimos después — Cross-Border Data Bridge, integraciones de cumplimiento — tú nos dices. Tú votas." },
      ],
      compareTitle: "FUNDADORES 10 vs PREMIUM ESTÁNDAR",
      compare: [
        ["Precio fijo de por vida en $149/mes", true, false],
        ["Visita y fotografía en planta", true, false],
        ["Perfil bilingüe hecho-por-nosotros", true, false],
        ["Destacado en el Supplier Digest mensual", true, false],
        ["Tope del directorio del corredor", true, false],
        ["Acceso WhatsApp al fundador", true, false],
        ["Voto en el roadmap", true, false],
        ["Cross-Border Data Bridge (KPIs partner EE.UU.)", true, true],
        ["Bóveda de documentos + enlaces", true, true],
        ["Bandeja RFQ", true, true],
        ["Perfil bilingüe ES/EN", true, true],
      ],
      caseStudy: "CASO · INDUSTRIAS FRONTERA MX",
      caseStudyQuote: "Nos dijeron que nadie le apunta a las maquiladoras. Nosotros dijimos — exacto.",
      caseStudyAttrib: "— Derick Varela, Fundador · Apex Digital",
      caseStudyStats: [
        ["$2.4M", "Valor del contrato inicial"],
        ["6 semanas", "De visita en planta a plataforma bilingüe"],
        ["12", "Keywords en página 1, EN + ES"],
        ["4", "Cotizaciones calificadas en primeros 30 días"],
      ],
      formTitle: "Reserva tu lugar fundador",
      formSub: "Aplicar no te compromete. Pone tu planta frente a nosotros para revisión. Respondemos en 48 horas.",
      submittedTitle: "Aplicación recibida.",
      submittedBody: "Revisaremos y respondemos en 48 horas. Si quedas seleccionado, recibirás un link para una llamada con Derick.",
      f: {
        contact_name: "Tu nombre",
        contact_email: "Correo",
        company_name: "Nombre de la empresa",
        role: "Tu rol (ej. Director General, Gerente Comercial)",
        plant_location: "Ubicación de la planta (ciudad / parque industrial)",
        employees: "Número de empleados",
        main_capabilities: "Capacidades principales (ej. CNC, inyección, arneses)",
        why_founding: "¿Por qué quieres ser uno de los primeros 10? (sé honesto, esto sí lo leemos)",
        phone: "WhatsApp / teléfono",
      },
      submit: "Enviar aplicación",
      footerNote: "El precio fundador termina cuando se llene el lugar #10. Sin excepciones. Sin extensiones.",
    },
  };
  const c = copy[lang];

  return (
    <div data-testid="founding-page">
      {/* Hero with scarcity */}
      <section className="relative" style={{ borderBottom: "1px solid var(--line)", background: "var(--ink)", color: "white" }}>
        <div className="absolute inset-0 grid-skeleton opacity-15" />
        <div className="relative container-cd py-20 md:py-32">
          <div className="overline" style={{ color: "var(--orange)" }}>
            <Lock size={11} className="inline mr-2 -mt-1" /> {c.kicker}
          </div>
          <h1
            className="font-display mt-6 leading-[0.92] tracking-tighter whitespace-pre-line"
            style={{ fontSize: "clamp(40px, 7vw, 96px)" }}
            data-testid="founding-title"
          >
            {c.title}
          </h1>
          <p className="mt-8 text-lg md:text-xl max-w-2xl" style={{ color: "rgba(255,255,255,0.7)" }}>
            {c.sub}
          </p>

          {/* Scarcity bar */}
          <div className="mt-12 max-w-2xl" data-testid="founding-scarcity">
            <div className="flex justify-between items-baseline mb-3">
              <div className="overline" style={{ color: "rgba(255,255,255,0.6)" }}>{c.spotsLeftL}</div>
              <div className="font-display text-4xl">
                <span style={{ color: "var(--orange)" }}>{spotsLeft}</span>
                <span className="text-sm font-mono opacity-50"> / 10</span>
              </div>
            </div>
            <div className="h-3 w-full" style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.3)" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: "var(--orange)" }} />
            </div>
            {spotsClaimed > 0 && (
              <div className="mt-3 font-mono text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>
                {spotsClaimed} {lang === "es" ? "aplicaciones recibidas en las últimas semanas" : "applications received in the last weeks"}
              </div>
            )}
          </div>

          {/* Anchor pricing — $299 strikethrough → $149 prominent */}
          <div
            className="mt-10 max-w-2xl flex flex-col items-start gap-1"
            data-testid="founding-anchor-price"
          >
            <div
              className="font-mono text-xl md:text-2xl"
              style={{
                color: "rgba(255,255,255,0.45)",
                textDecoration: "line-through",
                textDecorationThickness: "2px",
                textDecorationColor: "rgba(255,107,0,0.55)",
                letterSpacing: "0.02em",
              }}
              data-testid="founding-anchor-price-old"
            >
              $299<span className="text-sm opacity-80">/mo</span>
            </div>
            <div
              className="font-display leading-none inline-flex items-baseline gap-2"
              style={{
                fontSize: "clamp(48px, 6vw, 80px)",
                color: "var(--orange)",
                filter: "drop-shadow(0 0 26px rgba(255,107,0,0.35))",
              }}
              data-testid="founding-anchor-price-new"
            >
              $149
              <span
                className="font-mono text-base md:text-lg"
                style={{ color: "rgba(255,255,255,0.7)" }}
              >
                /{lang === "es" ? "mes" : "mo"}
              </span>
            </div>
            <div
              className="font-mono text-xs uppercase tracking-wider mt-1"
              style={{ color: "rgba(255,255,255,0.55)" }}
            >
              {lang === "es"
                ? "Precio fundador — fijo de por vida"
                : "Founding price — locked for life"}
            </div>
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="section-cd">
        <div className="container-cd">
          <div className="overline">{c.sectionWhy}</div>
          <div className="mt-10 grid md:grid-cols-2 gap-0" style={{ border: "1px solid var(--line)" }}>
            {c.reasons.map((r, i) => (
              <div
                key={i}
                className="p-8"
                style={{
                  background: "white",
                  borderRight: i % 2 === 0 ? "1px solid var(--line)" : "none",
                  borderBottom: i < c.reasons.length - 2 ? "1px solid var(--line)" : "none",
                }}
              >
                <div className="overline">0{i + 1}</div>
                <h3 className="font-heading text-2xl font-semibold mt-3">{r.t}</h3>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--ink-2)" }}>{r.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compare table */}
      <section className="section-cd" style={{ background: "var(--surface)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
        <div className="container-cd">
          <div className="overline">{c.compareTitle}</div>
          <div className="cd-card p-0 mt-8 overflow-x-auto">
            <table className="cd-table">
              <thead>
                <tr>
                  <th style={{ width: "60%" }}></th>
                  <th style={{ color: "var(--orange)" }}>FOUNDING 10</th>
                  <th>{lang === "es" ? "PREMIUM ESTÁNDAR" : "STANDARD PREMIUM"}</th>
                </tr>
              </thead>
              <tbody>
                {c.compare.map(([label, f, p], i) => (
                  <tr key={i}>
                    <td>{label}</td>
                    <td>{f ? <Check size={16} style={{ color: "var(--orange)" }} /> : <X size={16} style={{ color: "var(--ink-2)" }} />}</td>
                    <td>{p ? <Check size={16} /> : <X size={16} style={{ color: "var(--ink-2)" }} />}</td>
                  </tr>
                ))}
                <tr>
                  <td className="overline">{lang === "es" ? "PRECIO" : "PRICE"}</td>
                  <td className="font-display text-xl" style={{ color: "var(--orange)" }}>$149<span className="font-body text-xs">/mo · {lang === "es" ? "fijo" : "locked"}</span></td>
                  <td className="font-display text-xl">$599<span className="font-body text-xs">/mo</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Case study */}
      <section className="section-cd" style={{ background: "var(--ink)", color: "white" }}>
        <div className="container-cd">
          <div className="overline" style={{ color: "rgba(255,255,255,0.6)" }}>{c.caseStudy}</div>
          <blockquote
            className="mt-8 font-display text-3xl md:text-5xl leading-[1.1]"
            style={{ color: "white", maxWidth: 900 }}
          >
            "{c.caseStudyQuote}"
          </blockquote>
          <div className="mt-6 font-mono text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>{c.caseStudyAttrib}</div>
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-0" style={{ border: "1px solid rgba(255,255,255,0.2)" }}>
            {c.caseStudyStats.map(([v, l], i) => (
              <div
                key={i}
                className="p-6"
                style={{ borderRight: i < 3 ? "1px solid rgba(255,255,255,0.2)" : "none" }}
              >
                <div className="font-display text-4xl md:text-5xl" style={{ color: "var(--orange)" }}>{v}</div>
                <div className="overline mt-2" style={{ color: "rgba(255,255,255,0.6)" }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section id="apply" className="section-cd" style={{ background: "var(--bg)" }}>
        <div className="container-cd grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline">APPLY · 05</div>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[0.95]">
              {submitted ? c.submittedTitle : c.formTitle}
            </h2>
            <p className="mt-6 text-base" style={{ color: "var(--ink-2)" }}>
              {submitted ? c.submittedBody : c.formSub}
            </p>
            <div className="mt-10 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
              <Lock size={11} className="inline mr-2 -mt-1" /> {c.footerNote}
            </div>
          </div>
          <div className="md:col-span-7">
            {submitted ? (
              <div data-testid="founding-submitted">
                <div
                  className="cd-card p-8 text-center"
                  style={{ borderColor: "var(--orange)", borderWidth: 2 }}
                >
                  <div className="font-display text-5xl" style={{ color: "var(--orange)" }}>✓</div>
                  <div className="overline mt-4">RECEIVED · UNDER REVIEW</div>
                  <div className="mt-6 flex gap-3 justify-center flex-wrap no-print">
                    <button
                      onClick={() => window.print()}
                      className="cd-cta-btn-primary"
                      data-testid="founding-print-agreement"
                    >
                      <Printer size={14} />{" "}
                      {lang === "es" ? "Imprimir acuerdo fundador" : "Print founding agreement"}
                    </button>
                    <button onClick={() => nav("/")} className="btn btn-dark">
                      {lang === "es" ? "Volver al inicio" : "Back to home"}
                    </button>
                  </div>
                </div>

                {/* Printable Founding Member Agreement */}
                <article className="founding-agreement mt-10 cd-card p-10" data-testid="founding-agreement">
                  <div className="flex items-center justify-between pb-4" style={{ borderBottom: "2px solid var(--ink)" }}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center font-display text-white" style={{ background: "var(--ink)" }}>CD</div>
                      <div className="leading-tight">
                        <div className="font-display text-base">CORRIDOR DIGITAL</div>
                        <div className="overline" style={{ fontSize: 9 }}>FOUNDING 10 · MEMBER AGREEMENT</div>
                      </div>
                    </div>
                    <div className="text-right font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                      REF · CD-F10-{new Date().getFullYear()}
                      <br />
                      {new Date().toISOString().slice(0, 10)}
                    </div>
                  </div>

                  <h2 className="font-display text-3xl md:text-4xl mt-8 leading-tight">
                    {lang === "es"
                      ? "Acuerdo de Miembro Fundador"
                      : "Founding Member Agreement"}
                  </h2>

                  <p className="mt-6 text-sm md:text-base leading-relaxed">
                    {lang === "es" ? (
                      <>
                        Este documento confirma la aplicación de <strong>{form.company_name || "—"}</strong>{" "}
                        al programa <strong>Founding 10</strong> de Corridor Digital — el directorio bilingüe
                        IMMEX oficial del corredor Chihuahua–El Paso (Juárez, Parral, Chihuahua City). Operado por{" "}
                        <strong>{isLLCFormed ? LLC.name : LLC.pending_label}</strong>.
                      </>
                    ) : (
                      <>
                        This document confirms the application of <strong>{form.company_name || "—"}</strong>{" "}
                        to the Corridor Digital <strong>Founding 10</strong> program — the bilingual IMMEX
                        directory of record for the Chihuahua–El Paso manufacturing corridor (Juárez, Parral, Chihuahua City). Operated by{" "}
                        <strong>{isLLCFormed ? LLC.name : LLC.pending_label}</strong>.
                      </>
                    )}
                  </p>

                  <div className="mt-8 grid grid-cols-2 gap-6">
                    <div>
                      <div className="overline">{lang === "es" ? "FABRICANTE" : "MANUFACTURER"}</div>
                      <div className="mt-3 space-y-1 font-mono text-xs">
                        <div><strong>{form.company_name || "—"}</strong></div>
                        <div>{form.plant_location || "—"}</div>
                        <div>{form.employees || "—"} {lang === "es" ? "empleados" : "employees"}</div>
                        <div style={{ color: "var(--ink-2)" }}>
                          {lang === "es" ? "Contacto:" : "Contact:"} {form.contact_name || "—"}
                        </div>
                        <div style={{ color: "var(--ink-2)" }}>{form.contact_email || "—"}</div>
                        {form.phone ? <div style={{ color: "var(--ink-2)" }}>{form.phone}</div> : null}
                      </div>
                    </div>
                    <div>
                      <div className="overline">{lang === "es" ? "ENTIDAD OPERADORA" : "OPERATING ENTITY"}</div>
                      <div className="mt-3 space-y-1 font-mono text-xs">
                        <div><strong>{isLLCFormed ? LLC.name : LLC.pending_label}</strong></div>
                        <div>{LLC.registered_address_line_1}</div>
                        <div>{LLC.registered_address_line_2}</div>
                        <div style={{ color: "var(--ink-2)" }}>{LLC.contact_email}</div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <div className="overline">{lang === "es" ? "TÉRMINOS FUNDADORES" : "FOUNDING TERMS"}</div>
                    <ul className="mt-4 space-y-3 text-sm" style={{ color: "var(--ink)" }}>
                      <li className="flex gap-3"><Check size={16} style={{ color: "var(--orange)", flexShrink: 0, marginTop: 3 }} />
                        {lang === "es" ? (
                          <span><strong>Precio fundador de por vida:</strong> $149 USD / mes — bloqueado mientras la suscripción permanezca activa e ininterrumpida (vs $599 estándar Premium).</span>
                        ) : (
                          <span><strong>Lifetime founding price:</strong> $149 USD / month — locked for the duration of an uninterrupted, active subscription (vs $599 standard Premium).</span>
                        )}
                      </li>
                      <li className="flex gap-3"><Check size={16} style={{ color: "var(--orange)", flexShrink: 0, marginTop: 3 }} />
                        {lang === "es" ? (
                          <span><strong>Corredor Verificado desde el día uno</strong> — insignia inmediata, posición destacada en el directorio.</span>
                        ) : (
                          <span><strong>Corridor Verified from day one</strong> — immediate badge and featured directory placement.</span>
                        )}
                      </li>
                      <li className="flex gap-3"><Check size={16} style={{ color: "var(--orange)", flexShrink: 0, marginTop: 3 }} />
                        {lang === "es" ? (
                          <span><strong>Onboarding de alto contacto:</strong> visita en planta, fotografía, traducción de documentos y redacción del perfil bilingüe (6 semanas).</span>
                        ) : (
                          <span><strong>White-glove onboarding:</strong> onsite plant visit, photography, document translation, bilingual profile copywriting (6 weeks).</span>
                        )}
                      </li>
                      <li className="flex gap-3"><Check size={16} style={{ color: "var(--orange)", flexShrink: 0, marginTop: 3 }} />
                        {lang === "es" ? (
                          <span><strong>Posición permanente</strong> en el boletín mensual Corridor Digest a +1,200 compradores estadounidenses.</span>
                        ) : (
                          <span><strong>Permanent placement</strong> in the monthly Corridor Digest newsletter to 1,200+ US procurement contacts.</span>
                        )}
                      </li>
                      <li className="flex gap-3"><Check size={16} style={{ color: "var(--orange)", flexShrink: 0, marginTop: 3 }} />
                        {lang === "es" ? (
                          <span><strong>Línea WhatsApp directa</strong> con el fundador. Voto en el roadmap de producto.</span>
                        ) : (
                          <span><strong>Direct WhatsApp line</strong> with the founder. Roadmap voting rights.</span>
                        )}
                      </li>
                      <li className="flex gap-3"><X size={16} style={{ color: "var(--red)", flexShrink: 0, marginTop: 3 }} />
                        {lang === "es" ? (
                          <span><strong>Cancelación =</strong> pérdida del precio fundador. Resuscripción aplica precio estándar.</span>
                        ) : (
                          <span><strong>Cancellation =</strong> forfeit of founding pricing. Resubscription applies standard pricing.</span>
                        )}
                      </li>
                    </ul>
                  </div>

                  <p className="mt-8 text-sm" style={{ color: "var(--ink-2)" }}>
                    {lang === "es" ? (
                      <>
                        Este acuerdo no es vinculante hasta que el fabricante sea seleccionado y la
                        suscripción mensual se active vía Stripe. Sujeto a los Términos del Servicio,
                        Política de Privacidad y Política de Reembolsos publicados en{" "}
                        <strong>corridordigital.net/legal</strong>.
                      </>
                    ) : (
                      <>
                        This agreement is not binding until the manufacturer is selected and the
                        monthly subscription is activated via Stripe. Subject to the Terms of Service,
                        Privacy Policy and Refund Policy published at{" "}
                        <strong>corridordigital.net/legal</strong>.
                      </>
                    )}
                  </p>

                  <div className="mt-12 grid grid-cols-2 gap-12">
                    <div>
                      <div style={{ borderTop: "1px solid var(--ink)" }} className="pt-2 font-mono text-xs">
                        {form.contact_name || "—"}
                        <br />
                        <span style={{ color: "var(--ink-2)" }}>
                          {lang === "es" ? "Por" : "For"} {form.company_name || "—"}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div style={{ borderTop: "1px solid var(--ink)" }} className="pt-2 font-mono text-xs">
                        Derick Varela
                        <br />
                        <span style={{ color: "var(--ink-2)" }}>
                          {lang === "es" ? "Fundador · " : "Founder · "}
                          {isLLCFormed ? LLC.name : LLC.pending_label}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-10 pt-6 font-mono text-xs text-center" style={{ borderTop: "1px solid var(--line)", color: "var(--ink-2)" }}>
                    {lang === "es"
                      ? "Documento generado al recibir la aplicación. Guarda una copia para tus registros."
                      : "Document generated upon application receipt. Save a copy for your records."}
                  </div>
                </article>

                <style>{`
                  @media print {
                    .no-print { display: none !important; }
                    header, footer, [data-testid="site-footer"], [data-testid="main-nav"], [data-testid="cookie-banner"] { display: none !important; }
                    .founding-agreement { border: none !important; padding: 0 !important; }
                    body, .container-cd { background: white !important; }
                    @page { size: A4; margin: 14mm; }
                  }
                `}</style>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="cd-card p-8 grid md:grid-cols-2 gap-5" data-testid="founding-form">
                <Field label={c.f.contact_name} val={form.contact_name} on={(v) => set("contact_name", v)} req testid="founding-name" />
                <Field label={c.f.contact_email} val={form.contact_email} on={(v) => set("contact_email", v)} req type="email" testid="founding-email" />
                <Field label={c.f.company_name} val={form.company_name} on={(v) => set("company_name", v)} req full testid="founding-company" />
                <Field label={c.f.role} val={form.role} on={(v) => set("role", v)} testid="founding-role" />
                <Field label={c.f.plant_location} val={form.plant_location} on={(v) => set("plant_location", v)} testid="founding-location" />
                <Field label={c.f.employees} val={form.employees} on={(v) => set("employees", v)} testid="founding-employees" />
                <Field label={c.f.phone} val={form.phone} on={(v) => set("phone", v)} testid="founding-phone" />
                <Field label={c.f.main_capabilities} val={form.main_capabilities} on={(v) => set("main_capabilities", v)} full testid="founding-capabilities" />
                <div className="md:col-span-2">
                  <label className="overline">{c.f.why_founding}</label>
                  <textarea
                    className="cd-textarea mt-2"
                    value={form.why_founding}
                    onChange={(e) => set("why_founding", e.target.value)}
                    rows={4}
                    data-testid="founding-why"
                  />
                </div>
                {err ? (
                  <div className="md:col-span-2 font-mono text-xs px-3 py-2" style={{ background: "var(--red)", color: "white" }}>{err}</div>
                ) : null}
                <button type="submit" disabled={loading} className="md:col-span-2 cd-cta-btn-primary" data-testid="founding-submit">
                  {loading ? "…" : c.submit} <ArrowRight size={16} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, val, on, req, full, type = "text", testid }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="overline">{label}{req ? " *" : ""}</label>
      <input
        className="cd-input-box mt-2"
        value={val}
        onChange={(e) => on(e.target.value)}
        type={type}
        required={req}
        data-testid={testid}
      />
    </div>
  );
}
